import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ContainerComponent from 'app/shared-components/container-component/container-component';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  selectReviewOnboardSlice,
  fetchPlaidLinkedBankAccounts,
  PlaidLinkedBankAccountSafe,
} from 'app/pages/store/reviewOnboardSlice';
import { selectFundingSlice, getFinancialAccountDetails } from 'app/pages/store/fundingSlice';
import { HighnoteProductType } from 'API';
import {
  usePlaidBankAccountLink,
  PendingBankAccount,
  toPendingBankAccount,
} from 'app/pages/cards/credit/hooks/usePlaidBankAccountLink';
import { useBankLinkStepUpGate } from 'app/auth/useBankLinkStepUpGate';
import BankAccountLinkingDialogs from 'app/pages/cards/credit/BankAccountLinkingDialogs';
import { WidgetContainer } from 'app/shared-components/ui-components/WidgetContainer';
import { AddCircle } from '@mui/icons-material';
import LoadingOverlay from 'app/shared-components/util/LoadingOverlay';
import theme from 'app/theme';

interface AddMemeberFormProps {
  handleNext: () => void;
}

const ConnectBankForm = ({ handleNext }: AddMemeberFormProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { integration } = useAppSelector(selectReviewOnboardSlice);
  const { activeExternalBankAccounts } = useAppSelector(selectFundingSlice);

  // Check product type to determine correct page name for pending dialog
  const isCreditProduct = integration?.highnoteProduct?.type === HighnoteProductType.CREDIT;
  const returnPageName = isCreditProduct ? 'credit management page' : 'debit funding page';

  // Pending bank accounts - same as Credit.tsx
  const [pendingBankAccounts, setPendingBankAccounts] = useState<PendingBankAccount[]>([]);

  // Fetch pending bank accounts - works for both credit and debit
  const fetchPendingBankAccounts = useCallback(async () => {
    if (!integration?.paidolId) return;

    try {
      // Use secure query - access_token is never exposed
      const items = await dispatch(
        fetchPlaidLinkedBankAccounts({
          paidolId: integration.paidolId,
          status: 'pending',
        })
      ).unwrap();

      // Convert to legacy PendingBankAccount format for backward compatibility
      setPendingBankAccounts(
        (items || [])
          .filter((item): item is PlaidLinkedBankAccountSafe => item !== null)
          .map(toPendingBankAccount)
      );
    } catch {
      // Ignore errors
    }
  }, [integration?.paidolId, dispatch]);

  // Fetch pending accounts on mount - works for both credit and debit
  useEffect(() => {
    if (integration?.paidolId) {
      fetchPendingBankAccounts();
    }
  }, [integration?.paidolId, fetchPendingBankAccounts]);

  // Fetch active external bank accounts from Highnote - works for both credit and debit
  useEffect(() => {
    if (integration?.businessAccountHolderId && integration.businessAccountHolderId !== 'undefined') {
      dispatch(getFinancialAccountDetails(integration));
    }
  }, [integration, dispatch]);

  // Convert activeExternalBankAccounts to the same format as Credit.tsx passes to PaymentsWidget
  const accounts = activeExternalBankAccounts.map((acc) => ({
    id: acc.id!,
    label: `${acc.name ?? 'Account'} •••• ${acc?.externalBankAccountDetails?.last4 ?? ''} (${
      acc?.externalBankAccountDetails?.type ?? ''
    })`,
  }));

  // Plaid hook - EXACTLY like PaymentsWidget does it
  const {
    openPlaidLink,
    isReady: isPlaidReady,
    isLoading: isPlaidLoading,
    dialogState,
    closeDialog,
  } = usePlaidBankAccountLink({
    onSuccess: () => {
      fetchPendingBankAccounts();
    },
    onPendingCreated: () => {
      fetchPendingBankAccounts();
    },
    existingPendingAccounts: pendingBankAccounts,
    // Convert active accounts to the format expected by the hook - EXACTLY like PaymentsWidget
    existingActiveAccounts: accounts.map((acc) => {
      // Extract mask and type from the label format "Account Name •••• 1234 (checking)"
      const maskMatch = acc.label.match(/••••\s*(\d+)/);
      const typeMatch = acc.label.match(/\(([^)]+)\)$/);
      return {
        mask: maskMatch?.[1],
        type: typeMatch?.[1],
      };
    }),
  });

  // SEE-221: gate onboarding bank linking behind an SMS step-up for
  // single-factor (password) sessions.
  const { guard, modal: stepUpModal } = useBankLinkStepUpGate();

  const handlePlaidClick = () => {
    guard(openPlaidLink);
  };

  return (
    <Grid
      container
      justifyContent="center"
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        pt: 3,
      }}
    >
      <Grid item xs={12}>
        <ContainerComponent
          title={t('financialAccountFirstRun.title')}
          description={t('financialAccountFirstRun.description')}
        />
      </Grid>
      <Grid
        item
        xs={4}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Plaid flow for both credit and debit users */}
        <WidgetContainer
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            width: '565px',
            p: 3,
            gap: '24px',
          }}
        >
          <LoadingOverlay isLoading={isPlaidLoading} />
          <Button
            onClick={handlePlaidClick}
            disabled={!isPlaidReady || isPlaidLoading}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '200px',
              width: '200px',
              backgroundColor: '#D4E2FF',
            }}
          >
            <AddCircle sx={{ width: '48px', height: '48px' }} />
            <Typography variant="h6" sx={{ fontSize: '16px', pt: 1 }}>
              {t('linkFinancialAccount')}
            </Typography>
          </Button>
          <Box sx={{ width: '280px' }}>
            <Typography
              variant="h3"
              sx={{
                color: theme.palette.primary.main,
                pb: 1,
              }}
            >
              {t('connectFundingWidget.header')}
            </Typography>
            <Typography sx={{ color: theme.palette.primary.main, pb: 2 }}>
              Speedchain allows you to connect your external bank accounts to our platform.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Not all banks are eligible for connection. Please reach out to support@speedchain.com or
              directly to your account manager if you have any trouble connecting.
            </Typography>
          </Box>
        </WidgetContainer>
        <Button variant="text" size="small" onClick={handleNext} sx={{ mt: 5 }}>
          {t('illDoThisLater')}
        </Button>
      </Grid>

      {/* Bank Account Linking Dialogs for both Credit and Debit Users */}
      <BankAccountLinkingDialogs
        state={dialogState}
        returnPageName={returnPageName}
        onClose={() => {
          closeDialog();
          // Refresh accounts after dialog closes
          fetchPendingBankAccounts();
          if (integration?.businessAccountHolderId && integration.businessAccountHolderId !== 'undefined') {
            dispatch(getFinancialAccountDetails(integration));
          }
        }}
        onSuccessClose={() => {
          closeDialog();
          // Refresh accounts and advance to next step
          fetchPendingBankAccounts();
          if (integration?.businessAccountHolderId && integration.businessAccountHolderId !== 'undefined') {
            dispatch(getFinancialAccountDetails(integration));
          }
          // Advance to the next onboarding step
          handleNext();
        }}
      />

      {stepUpModal}
    </Grid>
  );
};

export default ConnectBankForm;
